import { type ChangeEvent, useEffect } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { EntityType } from '@polyai/stream/store/reducers/entities/types';
import Text from '@polyai/ui/components/atoms/Text';
import Toggle from '@polyai/ui/components/atoms/Toggle';

import { FormTextInput } from 'components/molecules/Form';
import * as Styled from '../EntitySidebar.styled';
import type { EntityFormProps, EntitySchemaProps } from '.';

interface Props {
  readonly: boolean;
}

export const NumberForm: React.FC<Props> = ({ readonly }) => {
  const { control, watch, setValue } = useFormContext<EntitySchemaProps>();

  const config = watch('config');

  const isNumberConfig = config?.$case === EntityType.NUMERIC;
  const isRangeEnabled = isNumberConfig && config?.value.hasRange;
  const hasDecimal = isNumberConfig && config?.value?.hasDecimal;

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  useEffect(() => {
    if (config?.$case === EntityType.NUMERIC) {
      if (!hasDecimal) {
        // When decimals are disabled, floor the values
        if (config.value.max) {
          setValue('config.value.max', Math.floor(Number(config?.value?.max)));
        }
        if (config.value.min) {
          setValue('config.value.min', Math.floor(Number(config?.value?.min)));
        }
      } else {
        // When decimals are enabled, limit to 2 decimal places
        if (config.value.max) {
          setValue(
            'config.value.max',
            parseFloat(Number(config?.value?.max).toFixed(2)),
          );
        }
        if (config.value.min) {
          setValue(
            'config.value.min',
            parseFloat(Number(config?.value?.min).toFixed(2)),
          );
        }
      }
    }
  }, [config, setValue, hasDecimal]);

  if (config?.$case !== EntityType.NUMERIC) {
    return null;
  }

  const handleMinValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (config?.value.hasDecimal) {
      setValue(
        'config.value.min',
        e.target.value
          ? parseFloat(Number(e.target.value).toFixed(2))
          : undefined,
      );
    } else {
      setValue(
        'config.value.min',
        e.target.value ? Math.floor(Number(e.target.value)) : undefined,
      );
    }
  };

  const handleMaxValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (config?.value.hasDecimal) {
      setValue(
        'config.value.max',
        e.target.value
          ? parseFloat(Number(e.target.value).toFixed(2))
          : undefined,
      );
    } else {
      setValue(
        'config.value.max',
        e.target.value ? Math.floor(Number(e.target.value)) : undefined,
      );
    }
  };

  return (
    <>
      <Controller
        control={control}
        name="config.value.hasDecimal"
        render={({ field }) => (
          <Styled.SwitchWrapper>
            <Toggle
              handleToggle={field.onChange}
              readonly={readonly}
              toggleOn={!!field.value}
            />
            <Text>Numbers may include decimals</Text>
          </Styled.SwitchWrapper>
        )}
      />

      <Controller
        control={control}
        name="config.value.hasRange"
        render={({ field }) => (
          <Styled.SwitchWrapper>
            <Toggle
              handleToggle={field.onChange}
              readonly={readonly}
              toggleOn={!!field.value}
            />
            <Text>Set number range</Text>
          </Styled.SwitchWrapper>
        )}
      />

      {isRangeEnabled && (
        <Styled.RangeWrapper>
          <FormTextInput<EntityFormProps>
            controlKey={'config.value.min' as any}
            data-test-id="entity-minimum-number"
            label="Minimum (optional)"
            placeholder="0"
            readOnly={readonly}
            type="number"
            value={config?.value.min}
            onChange={handleMinValueChange}
          />

          <FormTextInput<EntityFormProps>
            controlKey={'config.value.max' as any}
            data-test-id="entity-maximum-number"
            label="Maximum (optional)"
            placeholder="1,000,000"
            readOnly={readonly}
            type="number"
            value={config?.value.max}
            onChange={handleMaxValueChange}
          />
        </Styled.RangeWrapper>
      )}
    </>
  );
};
